/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmActivities.ts
 * PURPOSE: CRM activity feed from /api/v1/crm/activities
 * DEPENDENCIES: react, @shared/lib/frontend/api/crmApi, ../types/activity.types, ./crmEntityMappers
 * PUBLIC_API: useCrmActivities
 * SIDE_EFFECTS: HTTP
 * AGENT_SAFETY: SAFE
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { crmCreate, crmDelete, crmList, crmPatch } from '@shared/lib/frontend/api/crmApi';
import type { Activity } from '../types/activity.types';
import { mapCrmShellActivityToCrm } from './crmEntityMappers';

export type UseCrmActivitiesMutate = {
  addActivity: (activity: Omit<Activity, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }) => Promise<void>;
  patchActivity: (id: string, patch: Partial<Activity>) => Promise<void>;
  markComplete: (activityId: string) => Promise<void>;
  deleteActivity: (activityId: string) => Promise<void>;
};

export type UseCrmActivitiesResult = {
  data: Activity[];
  isLoading: boolean;
  error: string | null;
  mutate: UseCrmActivitiesMutate;
  refetch: () => Promise<void>;
};

function activityToCreateBody(
  activity: Omit<Activity, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    type: activity.type,
    subject: activity.subject,
    relatedEntityId: activity.relatedEntityId,
    relatedEntityType: activity.relatedEntityType,
    ownerId: activity.ownerId,
    dueAt: activity.dueAtISO,
    status: activity.status === 'complete' ? 'complete' : activity.status,
    notes: activity.notes ?? activity.subject,
  };
  if (activity.metadata !== undefined) {
    body.metadata = activity.metadata;
  }
  if (activity.outcome !== undefined) {
    body.outcome = activity.outcome;
  }
  return body;
}

export function useCrmActivities(): UseCrmActivitiesResult {
  const [data, setData] = useState<Activity[]>([]);
  const dataRef = useRef<Activity[]>([]);
  dataRef.current = data;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await crmList<Record<string, unknown>>('activities', { limit: 200 });
      const rows = Array.isArray(res.items) ? res.items : [];
      setData(rows.map((r) => mapCrmShellActivityToCrm(r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const patchActivity = useCallback(async (id: string, patch: Partial<Activity>) => {
    const prev = structuredClone(dataRef.current);
    setData((rows) =>
      rows.map((a) => (a.id === id ? { ...a, ...patch, updatedAtISO: new Date().toISOString() } : a))
    );
    try {
      const body: Record<string, unknown> = {};
      if (patch.subject !== undefined) body.subject = patch.subject;
      if (patch.notes !== undefined) body.notes = patch.notes;
      if (patch.metadata !== undefined) body.metadata = patch.metadata;
      if (patch.dueAtISO !== undefined) body.dueAt = patch.dueAtISO;
      if (patch.status !== undefined) body.status = patch.status === 'complete' ? 'complete' : patch.status;
      if (patch.type !== undefined) body.type = patch.type;
      if (Object.keys(body).length === 0) return;
      await crmPatch('activities', id, body);
    } catch (e) {
      setData(prev);
      throw e;
    }
  }, []);

  const addActivity = useCallback(
    async (activity: Omit<Activity, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }) => {
      const prev = structuredClone(dataRef.current);
      const now = new Date().toISOString();
      const tempId = activity.id ?? `temp-act-${Date.now()}`;
      const optimistic: Activity = {
        ...(activity as Activity),
        id: tempId,
        status: activity.status ?? 'open',
        createdAtISO: now,
        updatedAtISO: now,
      };
      setData((rows) => [...rows, optimistic]);
      try {
        const created = await crmCreate<Record<string, unknown>>('activities', activityToCreateBody(activity));
        const mapped = mapCrmShellActivityToCrm(created);
        setData((rows) => rows.map((a) => (a.id === tempId ? { ...mapped, subject: activity.subject } : a)));
      } catch (e) {
        setData(prev);
        throw e;
      }
    },
    []
  );

  const deleteActivity = useCallback(async (activityId: string) => {
    await crmDelete('activities', activityId);
    await refetch();
  }, [refetch]);

  const markComplete = useCallback(async (activityId: string) => {
    await patchActivity(activityId, { status: 'complete' });
  }, [patchActivity]);

  const mutate = useMemo(
    () => ({ addActivity, patchActivity, markComplete, deleteActivity }) satisfies UseCrmActivitiesMutate,
    [addActivity, patchActivity, markComplete, deleteActivity]
  );

  return { data, isLoading, error, mutate, refetch };
}
